/**
 * Typed API client for IoT Watchdog.
 *
 * All requests go through `apiFetch`, which:
 *  - prefixes the path with /api (proxied to :8080 in dev via vite.config.ts)
 *  - attaches the stored JWT in the Authorization header
 *  - throws ApiError on non-2xx responses
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const TOKEN_KEY = 'watchdog_token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken()
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init.headers ?? {}),
  }

  const res = await fetch(`/api${path}`, { ...init, headers })

  if (!res.ok) {
    let message = res.statusText
    try {
      const body = await res.json()
      if (body.error) message = body.error
    } catch {
      // ignore parse errors
    }
    throw new ApiError(res.status, message)
  }

  // 204 No Content or empty body
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T
  }

  return res.json() as Promise<T>
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export interface RegisterRequest {
  tenant_name: string
  email: string
  password: string
}

export interface AuthResponse {
  token: string
  api_key?: string
  tenant_id: number
  user_id: number
  role: string
}

export async function register(req: RegisterRequest): Promise<AuthResponse> {
  return apiFetch<AuthResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(req),
  })
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  return apiFetch<AuthResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
}

// ── Devices ───────────────────────────────────────────────────────────────────

export interface Device {
  id: number
  tenant_id: number
  external_id: string
  name: string
  device_type: string
  status: 'online' | 'offline' | 'degraded' | 'unknown'
  last_seen: string
  first_seen: string
  expected_interval_sec: number | null
  metadata: Record<string, string> | null
}

export interface DevicesResponse {
  devices: Device[]
  count: number
}

export async function listDevices(): Promise<DevicesResponse> {
  return apiFetch<DevicesResponse>('/v1/devices')
}

export interface DeviceDetail {
  device: Device
  metrics: string[]
}

export async function getDevice(id: number): Promise<DeviceDetail> {
  return apiFetch<DeviceDetail>(`/v1/devices/${id}`)
}

export interface TelemetryPoint {
  time: string
  value: number
}

export interface TelemetryResponse {
  device_id: number
  metric: string
  since: string
  points: TelemetryPoint[]
  count: number
}

export async function getTelemetry(
  deviceId: number,
  metric: string,
  since = '1h',
  points = 300,
): Promise<TelemetryResponse> {
  return apiFetch<TelemetryResponse>(
    `/v1/devices/${deviceId}/telemetry?metric=${encodeURIComponent(metric)}&since=${since}&points=${points}`,
  )
}

// ── Alerts ────────────────────────────────────────────────────────────────────

export interface AlertEvent {
  id: number
  rule_id: number | null
  device_id: number | null
  tenant_id: number
  severity: string
  message: string
  triggered_at: string
  resolved_at: string | null
  /** ForgeOps job this alert spawned (forgeops channel), if any. */
  forgeops_job_id?: string | null
}

/** URL of the SSE stream of an alert's ForgeOps automation lifecycle.
 *  EventSource can't set headers, so the JWT rides as a query param. */
export function automationStreamUrl(alertId: number): string {
  const token = getToken() ?? ''
  return `/api/v1/alerts/${alertId}/automation/stream?token=${encodeURIComponent(token)}`
}

export interface AlertsResponse {
  alerts: AlertEvent[]
  count: number
}

export async function listAlerts(): Promise<AlertsResponse> {
  return apiFetch<AlertsResponse>('/v1/alerts')
}

export async function resolveAlert(id: number): Promise<void> {
  return apiFetch<void>(`/v1/alerts/${id}/resolve`, { method: 'PATCH' })
}

// ── Alert Rules ───────────────────────────────────────────────────────────────

export type ConditionType = 'offline' | 'value_threshold' | 'battery_low' | 'anomaly'
export type Severity      = 'critical' | 'warning' | 'info'

export interface AlertRule {
  id: number
  tenant_id: number
  name: string
  condition_type: ConditionType
  condition_config: Record<string, unknown>
  channels: string[] | null
  severity: Severity
  enabled: boolean
  cooldown_sec: number
  created_at: string
}

export interface RulesResponse {
  rules: AlertRule[]
  count: number
}

export interface CreateRuleRequest {
  name: string
  condition_type: ConditionType
  severity: Severity
  cooldown_sec: number
  condition_config?: Record<string, unknown>
}

export async function listRules(): Promise<RulesResponse> {
  return apiFetch<RulesResponse>('/v1/rules')
}

export async function createRule(req: CreateRuleRequest): Promise<AlertRule> {
  return apiFetch<AlertRule>('/v1/rules', {
    method: 'POST',
    body: JSON.stringify(req),
  })
}

export async function deleteRule(id: number): Promise<void> {
  return apiFetch<void>(`/v1/rules/${id}`, { method: 'DELETE' })
}

// ── Overview ──────────────────────────────────────────────────────────────────

export interface RecentDevice {
  id: number
  external_id: string
  status: string
  last_seen: string
}

export interface RecentAlert {
  id: number
  device_id: number | null
  severity: string
  message: string
  fired_at: string
  resolved: boolean
}

export interface OverviewStats {
  total_devices: number
  online_devices: number
  offline_devices: number
  unknown_devices: number
  alerts_today: number
  alerts_total: number
  open_alerts: number
  recent_devices: RecentDevice[]
  recent_alerts: RecentAlert[]
}

export async function getOverview(): Promise<OverviewStats> {
  return apiFetch<OverviewStats>('/v1/overview')
}

// ── API Key ───────────────────────────────────────────────────────────────────

export interface RotateKeyResponse {
  api_key: string
}

export async function rotateAPIKey(): Promise<RotateKeyResponse> {
  return apiFetch<RotateKeyResponse>('/v1/keys/rotate', { method: 'POST' })
}
